import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { fetchAllPages } from './fetch-all-pages';

// Capa de datos de "Completar Prácticas" del instructor (ENG-LAYER-01).
// Consolidado desde routes/instructor/complete-lessons.data.ts y la mutación
// inline de _components/enrollment-lessons.tsx.

export type CourseOption = {
  id: number;
  title: string;
  status: string;
};

type EnrollmentForCourse = {
  id: number;
  user_id: number;
  status: string;
  progress: number;
  user: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
  };
};

export type EnrollmentProgress = {
  enrollment: {
    id: number;
    user_id: number;
    course_id: number;
    status: string;
    progress_percent: number;
    course: { title: string };
  };
  sections: Array<{
    id: number;
    title: string;
    order: number;
    lessons: Array<{
      id: number;
      title: string;
      type: string;
      order: number;
      is_completed: boolean;
      completed_at: string | null;
      evaluated_by_name: string | null;
      evaluation_notes: string | null;
    }>;
  }>;
  overall_percent: number;
};

export const PRACTICAL_TYPES = ['practical_record', 'practical_observation'];

export function useCourses() {
  return useQuery({
    queryKey: ['instructor', 'courses-for-completion'],
    queryFn: () => fetchAllPages<CourseOption>('/courses', { status: 'published' }),
  });
}

export function useCourseEnrollments(courseId: number) {
  return useQuery({
    queryKey: ['instructor', 'enrollments-for-course', courseId],
    queryFn: () =>
      fetchAllPages<EnrollmentForCourse>('/enrollments', {
        course_id: String(courseId),
        status: 'active',
      }),
    enabled: courseId > 0,
  });
}

export function useEnrollmentDetail(enrollmentId: number) {
  return useQuery({
    queryKey: ['enrollments', 'detail', enrollmentId],
    queryFn: () => api.get<EnrollmentProgress>(`/enrollments/${enrollmentId}/progress`),
    select: (res) => res.data,
    enabled: enrollmentId > 0,
  });
}

export function useCompleteLesson(enrollmentId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (lessonId: number) =>
      api.post(`/enrollments/${enrollmentId}/lessons/${lessonId}/complete`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['enrollments', 'detail', enrollmentId] });
      qc.invalidateQueries({ queryKey: ['instructor', 'enrollments-for-course'] });
    },
  });
}
